import React, { useState, useEffect, Fragment, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Col, Form, FormCheck } from 'react-bootstrap'
import Joyride, { STATUS } from 'react-joyride'
import validator from 'validator'
import { gql, useMutation } from '@apollo/client'
import { useForm } from '../../utils/form-hook'
import { usePasswordValidation } from '../../utils/password-validation-hook'
import { googleMapsPlaces } from '../../utils/google-maps-places'
import { TagsInput } from 'react-tag-input-component'
import ShowErrors from '../../components/partials/ShowErrors'
import Tooltip from '../common/Tooltip'
import PasswordInput from '../common/PasswordInput'
import { useOnboardingTour } from '../../utils/onboarding-tour-hook'
import Swal from 'sweetalert2'
import _ from 'lodash'
import { MOCK_LICENSE_FORM_INITIAL_VALUES } from './mockLicenseFormData'
import KeywordHelper from '@/components/campaigns/KeywordHelper'
import LicenseTextHelper from '@/components/campaigns/LicenseTextHelper'

const DELETE_DRAFT_CAMPAIGN = gql`
    mutation DeleteDraftCampaign($campaignId: ID!) {
        deleteDraftCampaign(campaignId: $campaignId)
    }
`

// Vite only injects variables prefixed `VITE_`, so the `VITEAPP_…` spellings previously
// used below were always undefined. That made the keyword count check evaluate
// `0 < undefined` - false - which silently blocked every keyword from being entered, and
// rendered "Maximum undefined keywords are allowed" in the hints. Reading through these
// constants means a missing or misnamed variable degrades to a sensible limit instead of
// disabling the field.
const MAX_KEYWORDS_NUMBER = Number(import.meta.env.VITE_MAX_KEYWORDS_NUMBER) || 5
// Fixed rather than read from VITE_MAX_KEYWORDS_LENGTH: environments set it to 5, which cut every keyword to five
// characters. 30 matches the longest keyword the backend suggests.
const MAX_KEYWORD_LENGTH = 30
const MIN_GEOLOCATION_RADIUS = 11000
const MAX_GEOLOCATION_RADIUS = 50000
const RADIUS_RANGE_MESSAGE = `Radius must be between ${MIN_GEOLOCATION_RADIUS} and ${MAX_GEOLOCATION_RADIUS} metres`
const DESCRIPTION_MIN_LENGTH = 77
const DESCRIPTION_MAX_LENGTH = 500

const USER_FIELDS = ['firstName', 'lastName', 'email', 'username', 'timeZoneOffset']

/** Form values with the fields of a saved license (Neo4j campaign) applied over `values`. */
const valuesFromData = (values, data) => {
    const updated = { ...values }

    USER_FIELDS.forEach((key) => {
        updated[key] = data.user[key] || ''
    })

    if (data.keywords) {
        updated.keywords = JSON.parse(data.keywords)
    }

    if (data.geolocation) {
        updated.geolocation = {
            latitude: data.geolocation.latitude,
            longitude: data.geolocation.longitude,
        }
    }

    const specialKeys = new Set([...USER_FIELDS, 'keywords', 'geolocation'])
    Object.keys(values).forEach((key) => {
        if (!specialKeys.has(key) && data[key]) {
            updated[key] = data[key]
        }
    })

    return updated
}

const CampaignForm = ({
    data,
    updateData,
    callback,
    errors,
    mutation,
    draftId,
    guidance = false,
    submitLabel,
    onFooterStateChange,
    campaignId,
}) => {
    const [showKeywordsLimitMessage, setShowKeywordsMessage] = useState(false)
    const [showKeywordLengthLimitMessage, setShowKeywordLengthLimitMessage] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [useMyEmail, setUseMyEmail] = useState(true)
    const navigate = useNavigate()
    const isDraft = data?.systemStatus === 'DRAFT'
    const normalizedSubmitLabel = Array.isArray(submitLabel)
        ? submitLabel
        : submitLabel
          ? [submitLabel, submitLabel]
          : null
    const saveButtonName = normalizedSubmitLabel || (data ? ['Update', 'Updating'] : ['Create', 'Creating'])
    const { handleFocusTour } = useOnboardingTour()

    // For deleting drafts
    const [deleteDraftCampaign] = useMutation(DELETE_DRAFT_CAMPAIGN)

    const [run, setRun] = useState(true)
    const formRef = useRef(null)
    const [steps] = useState([
        {
            target: '.campaign-step-1',
            content: 'Enter First Name of the License Manager',
        },
        {
            target: '.campaign-step-2',
            content: 'Enter Last Name of the License Manager',
        },
        {
            target: '.campaign-step-3',
            content: 'Enter a unique username to log in to the Dashboard app',
        },
        {
            target: '.campaign-step-4',
            content:
                'Enter email address to receive notifications. If you want to use your email address, then check "Use My Email"',
        },
        {
            target: '.campaign-step-5',
            content: 'Enter a password to log in to the Dashboard app',
        },
        {
            target: '.campaign-step-6',
            content: 'Repeat the password again to confirm you set it up correctly',
        },
        {
            target: '.campaign-step-7',
            content: 'Enter a detailed name for the license to easily distinguish it from your other licenses',
        },
        {
            target: '.campaign-step-8',
            content:
                'Enter the address for the point that will serve as the centre of the geolocation area for the license. Start typing address and then select it from the list',
        },
        {
            target: '.campaign-step-9',
            content: `Enter the radius in metres for the license's geolocation area, between ${MIN_GEOLOCATION_RADIUS} and ${MAX_GEOLOCATION_RADIUS} metres`,
        },
        {
            target: '.campaign-step-10',
            content:
                'Enter up to 5 keywords that best describe your license, pressing the Enter key after each keyword',
        },
        {
            target: '.campaign-step-10-a',
            content: `Enter a purpose of your license (${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters long) to help the app generate more accurate AI reports`,
        },
        {
            target: '.campaign-step-11',
            content: `Enter a brief description of your license (${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters long) to help the app generate more accurate AI reports`,
        },
        {
            target: '.campaign-step-12',
            content:
                'Set status of the license to Active because only active licenses aggregate data, generate AI reports and purchase media',
        },
        {
            target: '.campaign-step-13',
            content: 'Click this button to create your license',
        },
    ])

    const handleTourCallback = (data) => {
        const { status } = data
        handleFocusTour(data, steps)
        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setRun(false)
        }
    }

    // The limit message is shown only when a keyword beyond the limit is entered, not when the limit is reached.
    const validateKeywordsInput = (keyword, keywords) => {
        const allowed = keywords.length < MAX_KEYWORDS_NUMBER
        setShowKeywordsMessage(!allowed)
        return allowed
    }

    const limitKeywordInput = (event) => {
        const value = event.target.value
        if (value.length > MAX_KEYWORD_LENGTH) {
            event.target.value = value.slice(0, -1)
            setShowKeywordLengthLimitMessage(true)
        } else {
            setShowKeywordLengthLimitMessage(false)
        }
        validateKeywords(values.keywords)
    }

    const setKeywords = (keywords) => {
        setValue((prevState) => {
            const length = keywords.length
            if (keywords[length - 1]) {
                keywords[length - 1] = keywords[length - 1].replace(/\s+/g, ' ').trim().toLowerCase()
            }
            if (keywords[length - 1]?.length > MAX_KEYWORD_LENGTH) {
                keywords[length - 1] = keywords[length - 1].substring(0, MAX_KEYWORD_LENGTH)
            }

            // Remove duplicated keywords
            if (keywords.slice(0, length - 1).includes(keywords[length - 1])) {
                keywords = keywords.splice(0, length - 1)
            }

            return {
                ...prevState,
                keywords: keywords,
            }
        })
    }

    const addKeyword = (keyword) => {
        const normalised = keyword.replace(/\s+/g, ' ').trim().toLowerCase().substring(0, MAX_KEYWORD_LENGTH)
        setValue((prevState) => {
            if (!normalised || prevState.keywords.length >= MAX_KEYWORDS_NUMBER) return prevState
            if (prevState.keywords.includes(normalised)) return prevState
            return { ...prevState, keywords: [...prevState.keywords, normalised] }
        })
    }

    const setFieldText = (name) => (text) => {
        setValue((prevState) => ({ ...prevState, [name]: text.substring(0, DESCRIPTION_MAX_LENGTH) }))
    }

    const setLicenseOverview = (licenseOverview) => {
        setValue((prevState) => ({ ...prevState, licenseOverview }))
    }

    const toggleUseMyEmail = () => {
        setUseMyEmail((prevState) => {
            return !prevState
        })
        setValue((prevState) => {
            return {
                ...prevState,
                email: '',
            }
        })
    }

    const usernameValidation = () => {
        const inputUsername = document.getElementById('username')

        if (validator.isAlphanumeric(values.username)) {
            inputUsername.setCustomValidity('')
        } else {
            inputUsername.setCustomValidity('Only Alphanumeric Characters are allowed')
        }
    }

    const handleSubmit = (e) => {
        const shouldUseLocalSubmittingState = !submitLabel

        if (shouldUseLocalSubmittingState && isSubmitting) {
            e.preventDefault()
            return
        }

        // Local submitting state is only needed for create/publish flows.
        // The step-based "Next" flow should not get stuck in a loading state.
        if (shouldUseLocalSubmittingState) {
            setIsSubmitting(true)
        }

        // Saving a draft keeps it a draft. Completing one means choosing a subscription plan, which
        // happens in its own flow, so editing here no longer publishes and bills the campaign.

        // Call the original onSubmit which handles validation
        onSubmit(e)
    }

    const handleCancel = useCallback(() => {
        // If it's a draft, prompt for confirmation before discarding
        if (isDraft || draftId) {
            Swal.fire({
                title: 'Discard Draft?',
                text: 'You are about to discard this draft. This action cannot be undone.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, discard it',
                cancelButtonText: 'No, keep editing',
            }).then((result) => {
                if (result.isConfirmed) {
                    const campaignIdToDelete = draftId || data?.id
                    if (campaignIdToDelete) {
                        deleteDraftCampaign({
                            variables: { campaignId: campaignIdToDelete },
                        }).then(() => {
                            navigate('/campaigns')
                        })
                    } else {
                        navigate('/campaigns')
                    }
                }
            })
        } else {
            // Just navigate away for non-drafts
            navigate('/campaigns')
        }
    }, [data?.id, deleteDraftCampaign, draftId, isDraft, navigate])

    const formInitialValues = useMemo(() => {
        const defaults = {
            firstName: '',
            lastName: '',
            email: '',
            username: '',
            password: '',
            confirmPassword: '',
            name: '',
            userStatus: 'ACTIVE',
            systemStatus: isDraft ? 'DRAFT' : 'ACTIVE',
            notes: '',
            purpose: '',
            address: '',
            geolocation: {
                latitude: null,
                longitude: null,
            },
            radius: '',
            keywords: [],
            timeZoneOffset: null,
            location: null,
        }
        if (!_.isEmpty(data)) {
            // Start from the saved license so the keywords input never reports an empty list over it (in
            // development StrictMode replays its mount effect after the data effect below has run).
            return valuesFromData(defaults, data)
        }
        const useMock = import.meta.env.DEV && import.meta.env.VITE_MOCK_LICENSE_FORM === 'true'
        return useMock ? { ...defaults, ...MOCK_LICENSE_FORM_INITIAL_VALUES } : defaults
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDraft])

    const { onChange, onSubmit, values, validated, validateEmail, setValue } = useForm(callback, formInitialValues)

    const { passwordValidation } = usePasswordValidation(values)

    // Add License has no saved license yet: the AI helpers share one overview, saved with the license when created
    const licenseDraft = campaignId
        ? undefined
        : {
              fields: {
                  name: values.name,
                  address: values.address,
                  keywords: values.keywords,
                  purpose: values.purpose,
                  brief: values.notes,
              },
              overview: values.licenseOverview ?? null,
              onOverviewSaved: setLicenseOverview,
          }

    const validateKeywords = useCallback((keywords) => {
        const inputKeywords = document.getElementsByName('keywords')[0]
        if (!inputKeywords) return
        if (keywords.length > 0) {
            inputKeywords.setCustomValidity('')
        } else {
            inputKeywords.setCustomValidity('At least one keyword is required')
        }
    }, [])

    const [showRadiusLimitMessage, setShowRadiusLimitMessage] = useState(false)

    // An empty radius is left to `required`; any other value must be within the allowed range.
    useEffect(() => {
        const inputRadius = document.getElementById('radius')
        const radiusText = String(values.radius ?? '')
        const radiusValue = Number(radiusText)
        const outOfRange =
            radiusText !== '' && !(radiusValue >= MIN_GEOLOCATION_RADIUS && radiusValue <= MAX_GEOLOCATION_RADIUS)
        inputRadius.setCustomValidity(outOfRange ? RADIUS_RANGE_MESSAGE : '')
    }, [values.radius])

    // Values above the maximum are never accepted; values below the minimum are allowed while typing.
    const handleRadiusChange = (e) => {
        const inputValue = e.target.value

        if (inputValue !== '' && parseInt(inputValue) > MAX_GEOLOCATION_RADIUS) {
            setShowRadiusLimitMessage(true)
            return
        }

        onChange(e)
        setShowRadiusLimitMessage(false)
    }

    // Reset isSubmitting state when mutation completes
    useEffect(() => {
        if (!mutation.loading) {
            setIsSubmitting(false)
        }
    }, [mutation.loading])

    // Handle isSubmitting state based on form validation
    useEffect(() => {
        if (validated) {
            // Check if form is valid after validation runs
            const form = document.querySelector('form')
            if (form) {
                if (form.checkValidity()) {
                    // Form is valid, allow submission to proceed
                    setIsSubmitting(true)
                } else {
                    // Form is invalid, reset submitting state
                    setIsSubmitting(false)
                }
            }
        }
    }, [validated])

    useEffect(() => {
        if (values.location) {
            setValue((prevState) => {
                return {
                    ...prevState,
                    address: values.location.address || '',
                    geolocation: {
                        latitude: values.location.lat,
                        longitude: values.location.lng,
                    },
                    timeZoneOffset: values.location.offset || 0 * 60,
                }
            })
        }
    }, [values.location, setValue])

    useEffect(() => {
        googleMapsPlaces(
            setValue,
            document.getElementById('address'),
            document.getElementById('map'),
            document.getElementById('radius'),
            data,
            isDraft,
        )
    }, [data, setValue])

    useEffect(() => {
        if (values.keywords.length < MAX_KEYWORDS_NUMBER) setShowKeywordsMessage(false)
        validateKeywords(values.keywords)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [values.keywords.length])

    useEffect(() => {
        const inputGeoLocation = document.getElementById('address')

        if (
            values.geolocation.latitude !== null &&
            values.geolocation.longitude !== null &&
            values.timeZoneOffset !== null
        ) {
            inputGeoLocation.setCustomValidity('')
        } else {
            inputGeoLocation.setCustomValidity('Geo Location was not defined')
        }
    }, [values.geolocation, values.timeZoneOffset])

    const initializedFromData = useRef(false)

    useEffect(() => {
        if (_.isEmpty(data) || initializedFromData.current) return
        initializedFromData.current = true

        setValue((prevState) => valuesFromData(prevState, data))

        if (data.user.email) {
            setUseMyEmail(false)
        }
    }, [data, setValue])

    useEffect(() => {
        updateData(values)
    }, [values, updateData])

    // Check for default values that were added during draft autosave
    const isDefaultUsername = (username) => username && username.startsWith('draft_user_')
    const isDefaultCampaignName = (name) => name === 'Untitled Campaign' || name === 'Untitled License'
    const shouldShowLocalSubmittingState = !submitLabel && isSubmitting
    const footerSubmitLabel =
        mutation.loading || shouldShowLocalSubmittingState
            ? `${isDraft ? 'Publishing' : saveButtonName[1]}...`
            : isDraft
              ? 'Publish'
              : saveButtonName[0]

    const handleFooterSubmit = useCallback(() => {
        formRef.current?.requestSubmit()
    }, [])

    useEffect(() => {
        if (!onFooterStateChange) return
        onFooterStateChange({
            onSubmit: handleFooterSubmit,
            onCancel: handleCancel,
            submitDisabled: mutation.loading || shouldShowLocalSubmittingState,
            cancelDisabled: mutation.loading,
            submitLabel: footerSubmitLabel,
        })
    }, [
        footerSubmitLabel,
        handleCancel,
        handleFooterSubmit,
        mutation.loading,
        onFooterStateChange,
        shouldShowLocalSubmittingState,
    ])

    return (
        <Fragment>
            <Joyride
                steps={steps}
                run={guidance}
                continuous={false}
                showProgress={false}
                showSkipButton={false}
                callback={handleTourCallback}
                styles={{ options: { primaryColor: '#437eeb' } }}
            />
            <Form ref={formRef} noValidate validated={validated} onSubmit={handleSubmit}>
                <h6 className="mb-2">License Manager</h6>
                <div className="row">
                    <Form.Group className="col-md-6 form-group campaign-step-1">
                        <Form.Label htmlFor="fname" className="">
                            First Name
                        </Form.Label>
                        <Form.Control
                            required
                            type="text"
                            id="fname"
                            placeholder="Enter First Name"
                            name="firstName"
                            value={values.firstName}
                            onChange={onChange}
                        ></Form.Control>
                    </Form.Group>
                    <Form.Group className="col-md-6 form-group campaign-step-2">
                        <Form.Label htmlFor="lname" className="">
                            Last Name
                        </Form.Label>
                        <Form.Control
                            required
                            type="text"
                            id="lname"
                            placeholder="Enter Last Name"
                            name="lastName"
                            value={values.lastName}
                            onChange={onChange}
                        ></Form.Control>
                    </Form.Group>
                </div>
                <h6 className="mb-3 mt-2">License Login Details</h6>
                <div className="row">
                    <Form.Group className="col-md-6 form-group campaign-step-3">
                        <Form.Label htmlFor="username" className="d-flex align-items-center">
                            Username
                            <Tooltip text={'A unique username is required to log in to the Dashboard app'} />
                        </Form.Label>
                        <Form.Control
                            required
                            type="text"
                            id="username"
                            placeholder={isDraft ? 'Enter Username (required for publishing)' : 'Enter Username'}
                            name="username"
                            value={isDraft && isDefaultUsername(values.username) ? '' : values.username}
                            onChange={onChange}
                            onKeyUp={usernameValidation}
                        ></Form.Control>
                        <Form.Control.Feedback type="invalid">
                            {values.username.length > 0 ? 'Only Alphanumeric Characters are allowed' : ''}
                        </Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="col-md-6 form-group campaign-step-4">
                        <Form.Label htmlFor="email" className="d-flex align-items-center">
                            Email
                            <Tooltip
                                text={
                                    'Select the email address to receive notifications. You can use your own email or enter a different one'
                                }
                            />
                        </Form.Label>
                        <Form.Control
                            required
                            type="email"
                            id="email"
                            placeholder="Or Enter Another Email"
                            name="email"
                            value={values.email}
                            onChange={onChange}
                            disabled={useMyEmail}
                            onKeyUp={validateEmail}
                        ></Form.Control>
                        <Form.Check className="form-check-inline mt-2">
                            <FormCheck.Input
                                type="checkbox"
                                className="form-check-input"
                                id="useAdminEmail"
                                checked={useMyEmail}
                                onChange={toggleUseMyEmail}
                            />
                            <FormCheck.Label
                                className="form-check-label ms-1"
                                htmlFor="useAdminEmail"
                                name="useAdminEmail"
                            >
                                Use My Email
                            </FormCheck.Label>
                        </Form.Check>
                    </Form.Group>
                    <Form.Group className="col-md-6 form-group campaign-step-5">
                        <Form.Label htmlFor="password" className="d-flex align-items-center">
                            Password
                            <Tooltip
                                text={
                                    'Set up a password for logging in to the Dashboard app. Enter a new password to change the existing one'
                                }
                            />
                        </Form.Label>
                        <PasswordInput
                            required={!data || isDraft}
                            id="password"
                            placeholder={
                                data
                                    ? isDraft
                                        ? 'Enter Password to Publish'
                                        : 'Enter New Password to Update'
                                    : 'Enter Password'
                            }
                            name="password"
                            value={values.password}
                            onChange={onChange}
                            onKeyUp={passwordValidation}
                            autoComplete="new-password"
                        >
                            <Form.Control.Feedback type="invalid">
                                {values.password.length > 0 ? 'Weak Password' : ''}
                            </Form.Control.Feedback>
                        </PasswordInput>
                    </Form.Group>
                    <Form.Group className="col-md-6 form-group campaign-step-6">
                        <Form.Label htmlFor="confirmPassword" className="d-flex align-items-center">
                            Repeat Password
                            <Tooltip
                                text={
                                    'Set up a password for logging in to the Dashboard app. Enter a new password to change the existing one'
                                }
                            />
                        </Form.Label>
                        <PasswordInput
                            required={!data || isDraft}
                            type="password"
                            id="confirmPassword"
                            placeholder={
                                data
                                    ? isDraft
                                        ? 'Confirm Password'
                                        : 'Enter New Password to Update'
                                    : 'Confirm Password'
                            }
                            name="confirmPassword"
                            value={values.confirmPassword}
                            onChange={onChange}
                            onKeyUp={passwordValidation}
                            autoComplete="new-password"
                        >
                            <Form.Control.Feedback type="invalid">
                                {values.password !== values.confirmPassword ? 'Passwords do not match' : ''}
                            </Form.Control.Feedback>
                        </PasswordInput>
                    </Form.Group>
                </div>
                <h6 className="mb-3 mt-2">License Settings</h6>
                <div className="row">
                    <Col lg="6">
                        <Form.Group className="col-md-12 form-group campaign-step-7">
                            <Form.Label htmlFor="name" className="d-flex align-items-center">
                                Name
                                <Tooltip
                                    text={
                                        'Enter a detailed name for the license to easily distinguish it from other licenses'
                                    }
                                />
                            </Form.Label>
                            <Form.Control
                                required
                                type="text"
                                id="name"
                                placeholder={
                                    isDraft ? 'Enter License Name (required for publishing)' : 'Enter License Name'
                                }
                                name="name"
                                value={isDraft && isDefaultCampaignName(values.name) ? '' : values.name}
                                onChange={onChange}
                            ></Form.Control>
                        </Form.Group>
                        <Form.Group className="col-md-12 form-group campaign-step-8">
                            <Form.Label htmlFor="address" className="d-flex align-items-center">
                                Geo Location (Address)
                                <Tooltip
                                    text={
                                        'Enter the address for the point that will serve as the center of the geolocation area for the license'
                                    }
                                />
                            </Form.Label>
                            <Form.Control
                                required
                                type="text"
                                id="address"
                                placeholder="Enter Address"
                                name="address"
                                value={values.address}
                                onChange={onChange}
                            ></Form.Control>
                            <Form.Control.Feedback type="invalid">
                                {values.address?.length > 0 ? 'Address was not recognised' : ''}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="col-md-12 form-group campaign-step-9">
                            <Form.Label htmlFor="radius" className="d-flex align-items-center">
                                Radius (m)
                                <Tooltip
                                    text={`Enter the radius in metres for the license's geolocation area, between ${MIN_GEOLOCATION_RADIUS} and ${MAX_GEOLOCATION_RADIUS} metres`}
                                />
                            </Form.Label>
                            <Form.Control
                                required
                                type="number"
                                id="radius"
                                min={MIN_GEOLOCATION_RADIUS}
                                max={MAX_GEOLOCATION_RADIUS}
                                placeholder={
                                    isDraft
                                        ? 'Enter Radius in Metres (required for publishing)'
                                        : 'Enter Radius in Metres'
                                }
                                name="radius"
                                value={values.radius}
                                onChange={handleRadiusChange}
                            ></Form.Control>
                            <Form.Control.Feedback
                                type="invalid"
                                style={showRadiusLimitMessage ? { display: 'block' } : undefined}
                            >
                                {RADIUS_RANGE_MESSAGE}
                            </Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group
                            className={`col-md-12 form-group ${
                                validated && values.keywords.length === 0 && 'invalid'
                            } campaign-step-10`}
                        >
                            <Form.Label htmlFor="radius" className="d-flex align-items-center">
                                Keywords
                                <Tooltip
                                    text={
                                        'Enter up to 5 keywords that best describe your license, pressing the Enter key after each keyword'
                                    }
                                />
                            </Form.Label>
                            <TagsInput
                                id="keywords"
                                value={values.keywords}
                                onChange={setKeywords}
                                name="keywords"
                                placeHolder="Enter keywords"
                                beforeAddValidate={validateKeywordsInput}
                                onKeyUp={limitKeywordInput}
                                classNames={{
                                    input: 'tw:bg-slate-800 tw:border-slate-600 tw:text-white tw:!bg-transparent',
                                    tag: 'tw:bg-blue-600 tw:text-white tw:!bg-transparent',
                                }}
                            />
                            {showKeywordsLimitMessage && (
                                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                    Maximum {MAX_KEYWORDS_NUMBER} keywords are allowed
                                </Form.Control.Feedback>
                            )}
                            {showKeywordLengthLimitMessage && (
                                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                    Maximum {MAX_KEYWORD_LENGTH} characters are allowed
                                </Form.Control.Feedback>
                            )}
                            {validated && values.keywords.length === 0 && (
                                <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                                    At least one keyword is required
                                </Form.Control.Feedback>
                            )}
                            <KeywordHelper
                                campaignId={campaignId}
                                draft={licenseDraft}
                                keywords={values.keywords}
                                onAdd={addKeyword}
                                maxKeywords={MAX_KEYWORDS_NUMBER}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-12 form-group campaign-step-10-a">
                            <Form.Label htmlFor="notes" className="d-flex align-items-center">
                                License Purpose
                                <Tooltip
                                    text={`Enter a purpose of your license (${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters long) to help us generate more accurate reports`}
                                />
                            </Form.Label>
                            <Form.Control
                                required
                                as="textarea"
                                id="purpose"
                                rows="3"
                                name="purpose"
                                minLength={DESCRIPTION_MIN_LENGTH}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                                onChange={onChange}
                                value={values.purpose}
                            />
                            <span className="tw:block tw:text-end tw:text-xs tw:text-gray-400 tw:mt-1">
                                {values.purpose.length}/{DESCRIPTION_MAX_LENGTH}
                            </span>
                            <Form.Control.Feedback type="invalid">
                                {values.purpose.length < DESCRIPTION_MIN_LENGTH &&
                                    `Minimum ${DESCRIPTION_MIN_LENGTH} characters are required`}
                            </Form.Control.Feedback>
                            <LicenseTextHelper
                                campaignId={campaignId}
                                draft={licenseDraft}
                                field="purpose"
                                value={values.purpose}
                                onSelect={setFieldText('purpose')}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-12 form-group campaign-step-11">
                            <Form.Label htmlFor="notes" className="d-flex align-items-center">
                                License Short Brief
                                <Tooltip
                                    text={`Enter a brief description of your license (${DESCRIPTION_MIN_LENGTH}-${DESCRIPTION_MAX_LENGTH} characters long) to help us generate more accurate reports`}
                                />
                            </Form.Label>
                            <Form.Control
                                required
                                as="textarea"
                                id="notes"
                                rows="3"
                                name="notes"
                                minLength={DESCRIPTION_MIN_LENGTH}
                                maxLength={DESCRIPTION_MAX_LENGTH}
                                onChange={onChange}
                                value={values.notes}
                            />
                            <span className="tw:block tw:text-end tw:text-xs tw:text-gray-400 tw:mt-1">
                                {values.notes.length}/{DESCRIPTION_MAX_LENGTH}
                            </span>
                            <Form.Control.Feedback type="invalid">
                                {values.notes.length < DESCRIPTION_MIN_LENGTH &&
                                    `Minimum ${DESCRIPTION_MIN_LENGTH} characters are required`}
                            </Form.Control.Feedback>
                            <LicenseTextHelper
                                campaignId={campaignId}
                                draft={licenseDraft}
                                field="brief"
                                value={values.notes}
                                onSelect={setFieldText('notes')}
                            />
                        </Form.Group>
                        <Form.Group className="col-md-12 form-group campaign-step-12">
                            <Form.Label htmlFor="userStatus" className="d-flex align-items-center">
                                License Status
                                <Tooltip
                                    text={
                                        'Only active licenses aggregate data, generate AI reports, and purchase media'
                                    }
                                />
                            </Form.Label>
                            <div className="d-flex flex-column gap-2">
                                <Form.Check className="form-check-inline">
                                    <FormCheck.Input
                                        type="radio"
                                        name="userStatus"
                                        id="statusActive"
                                        value="ACTIVE"
                                        checked={values.userStatus === 'ACTIVE'}
                                        onChange={onChange}
                                    />
                                    <FormCheck.Label htmlFor="statusActive" className="ms-1">
                                        Active
                                    </FormCheck.Label>
                                </Form.Check>
                                <Form.Check className="form-check-inline">
                                    <FormCheck.Input
                                        type="radio"
                                        name="userStatus"
                                        id="statusInactive"
                                        value="INACTIVE"
                                        checked={values.userStatus === 'INACTIVE'}
                                        onChange={onChange}
                                    />
                                    <FormCheck.Label htmlFor="statusInactive" className="ms-1">
                                        Inactive
                                    </FormCheck.Label>
                                </Form.Check>
                            </div>
                        </Form.Group>
                    </Col>
                    <Col lg="6" className="mt-5">
                        <div id="map" style={{ height: '556px' }}></div>
                    </Col>
                </div>
                {errors && errors.length > 0 && (
                    <div className="pb-3 pt-3">
                        <ShowErrors errors={errors} />
                    </div>
                )}
                {isDraft && (
                    <div className="text-muted d-flex align-items-center gap-2 py-3 border-top">
                        <small>
                            <i className="fas fa-info-circle mr-1"></i>
                            This license is currently saved as a draft. Click 'Publish' to make it active.
                        </small>
                    </div>
                )}
            </Form>
        </Fragment>
    )
}

export default CampaignForm
